import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EditorialPostCard from '../../molecules/EditorialPostCard';
import TimelineEntityCard from '../TimelineEntityCard';
import SuggestedUserCard from '../SuggestedUserCard';
import CardActionsMenu from '../../molecules/CardActionsMenu';
import ContentWithLinkPreviews from '../../molecules/ContentWithLinkPreviews';
import PostLinkPreview from '../../molecules/PostLinkPreview';
import { extractPrimaryLinkFromText } from '../../../utils/linkDetection';
import { isSupportedLinkPreviewUrl } from '../../molecules/LinkWithPreview/supportedLinkPreview.ts';
import PostDetailModal from '../PostDetailModal';
import ScheduledEntityCommentsModal from '../../molecules/ScheduledEntityCommentsModal';
import EventCreateModal from '../../molecules/EventCreateModal';
import ConfirmDialog from '../../molecules/ConfirmDialog';
import EditorialEmptyState from '../../molecules/EditorialEmptyState';
import UserCardAvatar from '../../molecules/UserCardAvatar';
import VirtualizedItem from '../../molecules/VirtualizedItem';
import { usePostDeleteDialog } from '../../../hooks/usePostDeleteDialog';
import { useDropdown } from '../../../hooks/useDropdown';
import { useClickableLinks } from '../../../hooks/useClickableLinks';
import useTimelineEngagement from '../../../hooks/useTimelineEngagement';
import useLocaleDateFormatter from '../../../hooks/useLocaleDateFormatter';
import createDeletePostMessageBuilder from '../../../utils/deletePostMessage';
import { normalizePostBody } from '../../../utils/postBody';
import { findFirstImageAttachmentUrl } from '../../../utils/imageUtils';
import { formatLastSeenRelative } from '../../../utils/presenceUtils';
import { postsService } from '../../../services/api/posts';
import { TIMELINE_ITEM_KINDS } from '../../../services/api/timeline';
import { isPostTimelineItem } from '../../../utils/timelineItems';
import { resolvePostCardIdentity } from '../../../utils/postCardIdentity';
import classNames from '../../../utils/classNames';
import bandIcon from '../../../images/band-icon.svg';
import './index.scss';

const SpotlightIcon = () => (
  <svg
    className="editorial-feed__badge-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2l2.6 6.6L22 10l-5.5 4.7L18.2 22 12 18l-6.2 4 1.7-7.3L2 10l7.4-1.4L12 2z" />
  </svg>
);

const canViewerManagePost = (post) => post?.canManage === true;

const isPinnedPost = (post) => (
  post?.pinned === true
  || post?.isPinned === true
  || Boolean(post?.pinnedAt)
);

/**
 * EditorialFeedList — single column of editorial timeline cards (posts +
 * entities) with the same look-and-feel as the home page's posts section.
 * Owns: like/comment/visibility/edit/delete handlers, PostDetailModal
 * lifecycle and the delete-confirm dialog. Composer is intentionally NOT
 * included — pages compose their own. Atomic pieces (EditorialPostCard,
 * LikeButton, CommentToggle, PostVisibilityControl, etc.) are reused
 * unchanged from the home page.
 */
export default function EditorialFeedList({
  items = [],
  currentUserId,
  onPostMetaChange,
  onDeletePost,
  onDeleteEntity,
  onEditPost,
  emptyTitle = 'Nothing in your feed yet',
  emptyDescription = "When your friends or bands post something, you'll see it here.",
  loadMore,
  hasMore = false,
  isLoadingMore = false,
  loadMoreLabel = 'Load more',
  loadingMoreLabel = 'Loading...',
  loadMoreSentinelRef,
  virtualizationRootRef,
}) {
  const { t } = useTranslation();
  const { formatDateTime, formatDate, formatTime } = useLocaleDateFormatter();
  const { dropdownOpenId, toggleDropdown, setDropdownOpenId } = useDropdown();
  const [editingScheduledEntity, setEditingScheduledEntity] = useState(null);
  const {
    openPostId,
    setOpenPostId,
    openScheduledEntityCommentsId,
    setOpenScheduledEntityCommentsId,
    renderSocialFooter,
  } = useTimelineEngagement({
    currentUserId,
    onItemMetaChange: onPostMetaChange,
  });
  const [inlineEditState, setInlineEditState] = useState({ postId: null, draft: '' });
  const [savingInlineEditId, setSavingInlineEditId] = useState(null);
  const renderPostBodyWithLinks = useClickableLinks();

  const timelineItems = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  );
  const postItems = useMemo(
    () => timelineItems.filter(isPostTimelineItem),
    [timelineItems]
  );

  const deleteMessageBuilder = useMemo(() => createDeletePostMessageBuilder(t), [t]);
  const {
    state: deleteDialogState,
    requestDelete: handleRequestDeletePost,
    cancelDelete: handleCancelDeletePost,
    confirmDelete: handleConfirmDeletePost,
    message: deleteDialogMessage,
  } = usePostDeleteDialog(postItems, onDeletePost, { buildMessage: deleteMessageBuilder });

  const handleStartInlineEdit = useCallback((post) => {
    if (!post?.id) return;
    setDropdownOpenId(null);
    setInlineEditState({
      postId: post.id,
      draft: normalizePostBody(post.body),
    });
  }, [setDropdownOpenId]);

  const handleCancelInlineEdit = useCallback(() => {
    if (savingInlineEditId) return;
    setInlineEditState({ postId: null, draft: '' });
  }, [savingInlineEditId]);

  const handleInlineDraftChange = useCallback((event) => {
    const nextValue = event.target.value;
    setInlineEditState((state) => ({
      ...state,
      draft: nextValue,
    }));
  }, []);

  const handleSaveInlineEdit = useCallback(async (post) => {
    if (!post?.id || savingInlineEditId) return;
    const nextBody = inlineEditState.draft.trim();
    const originalBody = normalizePostBody(post.body).trim();
    if (!nextBody) return;
    if (nextBody === originalBody) {
      setInlineEditState({ postId: null, draft: '' });
      return;
    }

    setSavingInlineEditId(post.id);
    try {
      const updated = await postsService.updatePost(post.id, {
        ...post,
        body: nextBody,
      });
      setInlineEditState({ postId: null, draft: '' });
      onPostMetaChange?.(post.id, {
        body: updated.body,
        visibility: updated.visibility,
        visibilityLabel: updated.visibilityLabel,
        updatedAt: updated.updatedAt,
      });
    } catch (error) {
      console.error('Failed to update post body', error);
    } finally {
      setSavingInlineEditId(null);
    }
  }, [inlineEditState.draft, onPostMetaChange, savingInlineEditId]);

  const handleInlineEditorKeyDown = useCallback((event, post) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelInlineEdit();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSaveInlineEdit(post);
    }
  }, [handleCancelInlineEdit, handleSaveInlineEdit]);

  const editLabel = t('home.postCard.actions.edit');
  const deleteLabel = t('home.postCard.actions.delete');
  const editInputLabel = t('home.postCard.editor.ariaLabel', 'Edit post content');
  const cancelEditLabel = t('home.postCard.editor.cancelLabel', 'Cancel edit');
  const saveEditLabel = t('home.postCard.editor.saveLabel', 'Save post');

  const buildSocialFooter = useCallback((item) => renderSocialFooter(item, {
    commentTriggerClassName: 'editorial-feed__comment-trigger',
    visibilityWrapperClassName: 'editorial-feed__foot-visibility',
  }), [renderSocialFooter]);

  const handleStartEditingScheduledEntity = useCallback((item) => {
    if (item?.objectType !== 'EVENT') return;
    setEditingScheduledEntity(item);
  }, []);

  return (
    <div className="editorial-feed">
      {timelineItems.length > 0 ? (
        <div className="editorial-feed__list">
          {timelineItems.map((post) => {
            if (post?.timelineItemKind === TIMELINE_ITEM_KINDS.SUGGESTED_USER) {
              return (
                <VirtualizedItem
                  key={post.id}
                  rootRef={virtualizationRootRef}
                  estimateHeight={360}
                  className="editorial-feed__virtual-item"
                >
                  <div className="editorial-feed__item editorial-feed__item--suggested-user">
                    <SuggestedUserCard item={post} currentUserId={currentUserId} />
                  </div>
                </VirtualizedItem>
              );
            }

            if (!isPostTimelineItem(post)) {
              return (
                <VirtualizedItem
                  key={post.id}
                  rootRef={virtualizationRootRef}
                  estimateHeight={360}
                  className="editorial-feed__virtual-item"
                >
                  <div className="editorial-feed__item editorial-feed__item--entity">
                    <TimelineEntityCard
                      item={post}
                      currentUserId={currentUserId}
                      dropdownOpenId={dropdownOpenId}
                      onToggleDropdown={toggleDropdown}
                      onDeleteEntity={onDeleteEntity}
                      onEditEntity={post.objectType === 'EVENT' ? handleStartEditingScheduledEntity : undefined}
                      socialFooter={buildSocialFooter(post)}
                    />
                  </div>
                </VirtualizedItem>
              );
            }

            const normalizedBody = normalizePostBody(post.body);
            const rendered = renderPostBodyWithLinks(normalizedBody);
            const primaryLink = extractPrimaryLinkFromText(normalizedBody);
            const shouldRenderFeaturedLink = Boolean(primaryLink) && !isSupportedLinkPreviewUrl(primaryLink);
            const mediaUrl = findFirstImageAttachmentUrl(post);
            const isPinned = isPinnedPost(post);
            const typeLabel = isPinned ? 'Pinned' : (mediaUrl ? 'Photo' : null);
            const canManage = canViewerManagePost(post);
            const isInlineEditing = inlineEditState.postId === post.id;
            const isSavingInlineEdit = savingInlineEditId === post.id;
            const inlineDraft = isInlineEditing ? inlineEditState.draft : normalizedBody;
            const trimmedInlineDraft = inlineDraft.trim();
            const canSaveInlineEdit = trimmedInlineDraft.length > 0
              && trimmedInlineDraft !== normalizedBody.trim();

            const {
              displayName,
              avatarUrl,
              enableProfilePreview,
              showSuggestedBadge,
              showOwnerBadge,
              relatedBand,
            } = resolvePostCardIdentity(post, currentUserId);
            const showContextBadge = showSuggestedBadge || showOwnerBadge;
            const contextBadgeKey = showOwnerBadge
              ? 'home.postCard.badges.owner'
              : 'home.postCard.badges.suggestedForYou';
            const formattedTimestamp = formatDateTime(post?.createdAt, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
            const compactTimestamp = (() => {
              if (!post?.createdAt) return '';
              const datePart = formatDate(post.createdAt, { year: undefined, month: 'short' });
              const timePart = formatTime(post.createdAt);
              if (datePart && timePart) return `${datePart} · ${timePart}`;
              return datePart || timePart || '';
            })();
            const relativeTimestamp = post?.createdAt ? formatLastSeenRelative(post.createdAt) : '';

            const actions = [];
            if (canManage) {
              actions.push({
                id: 'edit',
                label: editLabel,
                onSelect: () => {
                  setDropdownOpenId(null);
                  if (typeof onEditPost === 'function') {
                    onEditPost(post);
                    return;
                  }
                  handleStartInlineEdit(post);
                },
                disabled: Boolean(savingInlineEditId),
              });
            }
            if (canManage && onDeletePost) {
              actions.push({
                id: 'delete',
                label: deleteLabel,
                onSelect: () => {
                  setDropdownOpenId(null);
                  handleRequestDeletePost(post);
                },
              });
            }

            const kebab = !isInlineEditing && actions.length > 0 ? (
              <CardActionsMenu
                isOpen={dropdownOpenId === post.id}
                onToggle={toggleDropdown}
                itemId={post.id}
                actions={actions}
              />
            ) : null;

            const headerNode = (
              <div className="editorial-feed__header">
                <UserCardAvatar
                  userId={relatedBand ? undefined : (post.userId || undefined)}
                  username={displayName}
                  avatarUrl={relatedBand ? undefined : (avatarUrl || undefined)}
                  resolvedAvatar={relatedBand ? {
                    avatarUrl: avatarUrl || undefined,
                    shouldUseSupportAvatar: false,
                  } : undefined}
                  fallbackSrc={relatedBand ? bandIcon : undefined}
                  radius={relatedBand ? '14px' : undefined}
                  alt={`${displayName || (relatedBand ? 'Band' : 'User')} ${relatedBand ? 'image' : 'avatar'}`}
                  className={classNames('editorial-feed__avatar', {
                    'editorial-feed__avatar--band': Boolean(relatedBand),
                  })}
                  enableProfilePreview={enableProfilePreview}
                />
                <div className="editorial-feed__identity">
                  <div className="editorial-feed__identity-row">
                    <span className="editorial-feed__name">{displayName}</span>
                    {showContextBadge ? (
                      <span className="editorial-feed__badge editorial-feed__badge--spotlight">
                        <SpotlightIcon />
                        {t(contextBadgeKey)}
                      </span>
                    ) : null}
                    {typeLabel ? (
                      <span className="editorial-feed__type-tag">{typeLabel}</span>
                    ) : null}
                  </div>
                  <div className="editorial-feed__date-row">
                    {compactTimestamp ? (
                      <time
                        className="editorial-feed__timestamp"
                        dateTime={post.createdAt || undefined}
                        title={formattedTimestamp || undefined}
                      >
                        {compactTimestamp}
                      </time>
                    ) : null}
                    {compactTimestamp && relativeTimestamp ? (
                      <span className="editorial-feed__meta-dot" aria-hidden="true" />
                    ) : null}
                    {relativeTimestamp ? (
                      <span className="editorial-feed__timestamp editorial-feed__timestamp--relative">
                        {relativeTimestamp}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            );

            const bodyNode = normalizedBody ? (
              <ContentWithLinkPreviews previewUrls={rendered.previews}>
                <div className="editorial-feed__body" style={{ whiteSpace: 'pre-wrap' }}>
                  {rendered.content}
                </div>
              </ContentWithLinkPreviews>
            ) : null;
            const editorNode = isInlineEditing ? (
              <div className="editorial-feed__editor">
                <textarea
                  className="editorial-feed__editor-input"
                  aria-label={editInputLabel}
                  value={inlineDraft}
                  onChange={handleInlineDraftChange}
                  onKeyDown={(event) => handleInlineEditorKeyDown(event, post)}
                  disabled={isSavingInlineEdit}
                  rows={Math.max(4, Math.min(12, inlineDraft.split('\n').length + 1))}
                  autoFocus
                />
                <div className="editorial-feed__editor-actions" role="group" aria-label={t('home.postCard.editor.actionsLabel', 'Post edit actions')}>
                  <button
                    type="button"
                    className="editorial-feed__editor-button"
                    onClick={handleCancelInlineEdit}
                    disabled={isSavingInlineEdit}
                  >
                    {cancelEditLabel}
                  </button>
                  <button
                    type="button"
                    className="editorial-feed__editor-button editorial-feed__editor-button--save"
                    onClick={() => handleSaveInlineEdit(post)}
                    disabled={isSavingInlineEdit || !canSaveInlineEdit}
                  >
                    {saveEditLabel}
                  </button>
                </div>
              </div>
            ) : null;

            return (
              <VirtualizedItem
                key={post.id}
                rootRef={virtualizationRootRef}
                estimateHeight={mediaUrl ? 680 : 520}
                className="editorial-feed__virtual-item"
              >
                <div className="editorial-feed__item editorial-feed__item--post">
                  <EditorialPostCard
                    variant="text"
                    feature={isPinned}
                    topRight={kebab}
                    meta={headerNode}
                    footer={isInlineEditing ? null : buildSocialFooter(post)}
                  >
                    {editorNode}
                    {!isInlineEditing ? bodyNode : null}
                    {!isInlineEditing && shouldRenderFeaturedLink ? (
                      <PostLinkPreview
                        href={primaryLink}
                        className="editorial-feed__link-preview"
                      />
                    ) : null}
                    {!isInlineEditing && mediaUrl ? (
                      <div
                        className="editorial-feed__media"
                        style={{ backgroundImage: `url(${mediaUrl})` }}
                        role="img"
                        aria-label={`${displayName || 'Post'} attachment`}
                      />
                    ) : null}
                  </EditorialPostCard>
                </div>
              </VirtualizedItem>
            );
          })}
        </div>
      ) : (
        <EditorialEmptyState
          title={emptyTitle}
          description={emptyDescription}
        />
      )}

      {hasMore && typeof loadMore === 'function' ? (
        <div ref={loadMoreSentinelRef || undefined} className="editorial-feed__load-more">
          <button
            type="button"
            className="editorial-feed__load-more-btn"
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? loadingMoreLabel : loadMoreLabel}
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={deleteDialogState.isOpen}
        title={t('home.postCard.deleteDialog.title')}
        message={deleteDialogMessage}
        confirmLabel={t('home.postCard.deleteDialog.confirm')}
        cancelLabel={t('common.cancel')}
        processingLabel={t('home.postCard.deleteDialog.processing')}
        onConfirm={handleConfirmDeletePost}
        onCancel={handleCancelDeletePost}
        isProcessing={deleteDialogState.isProcessing}
        error={deleteDialogState.error || undefined}
        confirmVariant="danger"
      />

      {openPostId ? (() => {
        const openPost = postItems.find((p) => p.id === openPostId);
        if (!openPost) return null;
        return (
          <PostDetailModal
            post={openPost}
            currentUserId={currentUserId}
            onClose={() => setOpenPostId(null)}
            dropdownOpenId={dropdownOpenId}
            onToggleDropdown={toggleDropdown}
            onDelete={handleRequestDeletePost}
            onPostMetaChange={onPostMetaChange}
            onEditPost={typeof onEditPost === 'function'
              ? (post) => {
                  setOpenPostId(null);
                  onEditPost(post);
                }
              : undefined}
          />
        );
      })() : null}

      {openScheduledEntityCommentsId ? (() => {
        const openItem = timelineItems.find((item) => item.id === openScheduledEntityCommentsId);
        if (!openItem) return null;
        return (
          <ScheduledEntityCommentsModal
            item={openItem}
            currentUserId={currentUserId}
            onClose={() => setOpenScheduledEntityCommentsId(null)}
            onItemMetaChange={onPostMetaChange}
          />
        );
      })() : null}

      {editingScheduledEntity ? (
        <EventCreateModal
          isOpen
          mode="edit"
          event={editingScheduledEntity}
          onClose={() => setEditingScheduledEntity(null)}
          onUpdated={(updated) => {
            if (updated?.id) {
              onPostMetaChange?.(updated.id, updated);
            }
            setEditingScheduledEntity(null);
          }}
          onDeleted={(deleted) => {
            if (deleted?.id) {
              onDeleteEntity?.(deleted.id);
            }
            setEditingScheduledEntity(null);
          }}
        />
      ) : null}
    </div>
  );
}

EditorialFeedList.propTypes = {
  items: PropTypes.array,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPostMetaChange: PropTypes.func,
  onDeletePost: PropTypes.func,
  onDeleteEntity: PropTypes.func,
  onEditPost: PropTypes.func,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  loadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  loadMoreLabel: PropTypes.node,
  loadingMoreLabel: PropTypes.node,
  loadMoreSentinelRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  virtualizationRootRef: PropTypes.shape({
    current: PropTypes.any,
  }),
};
